import { ViewportElementProvider } from '@blocksuite/affine-shared/services';
import type { BlockStdScope } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';

import type { MinimapHost } from './minimap-canvas';

/** The maximap's least gap above a bottom overlay. */
const OVERLAY_GAP = 12;
/** Room the maximap keeps to each edge of its pane. */
const MAXIMAP_MARGIN = 24;
/** Width to height of the box the square maximap is sized in: a wide pane does not fill its height. */
const MAXIMAP_SIZING_ASPECT = 41 / 29;

/** Side of the square maximap in a pane of the given free size. */
export function maximapSize(width: number, height: number): number {
  return Math.max(
    0,
    Math.min(
      (width - MAXIMAP_MARGIN * 2) / MAXIMAP_SIZING_ASPECT,
      height - MAXIMAP_MARGIN * 2
    )
  );
}

/**
 * A pane the overview lies in without an editor of its own (a projection of a canvas, the workflow
 * builder): its element, and its viewport's overlays when it has a canvas viewport.
 */
export type MaximapPane = Pick<MinimapHost, 'element'> &
  Partial<Pick<MinimapHost, 'gfx'>>;

/** Keep the overlay inside this editor pane, above its visible bottom overlays (e.g. the dock). */
export function observeMaximapLayout(
  std: BlockStdScope | undefined,
  dialog: HTMLDialogElement,
  projection?: MaximapPane
): () => void {
  const host = projection?.element ?? std!.host;
  const viewport =
    projection?.element ??
    std?.getOptional(ViewportElementProvider)?.viewportElement ??
    host;
  const gfx = projection?.gfx ?? std?.getOptional(GfxControllerIdentifier);
  const doc = dialog.ownerDocument;
  const win = doc.defaultView!;
  // Floating and overlay sidebars lie over the editor instead of reducing its layout width.
  // Reuse ResizePanel's existing edge/open contract for both.
  const sidebars = Array.from(
    doc.querySelectorAll<HTMLElement>(
      '[data-is-floating][data-handle-position]'
    )
  );
  let frame: number | null = null;

  const update = () => {
    const pane = viewport.getBoundingClientRect();
    const editor = host.getBoundingClientRect();
    const visual = win.visualViewport;
    const screenLeft = visual?.offsetLeft ?? 0;
    const screenTop = visual?.offsetTop ?? 0;
    let left = Math.max(screenLeft, pane.left, editor.left);
    const top = Math.max(screenTop, pane.top, editor.top);
    let right = Math.min(
      screenLeft + (visual?.width ?? win.innerWidth),
      pane.right,
      editor.right
    );
    let bottom = Math.min(
      screenTop + (visual?.height ?? win.innerHeight),
      pane.bottom,
      editor.bottom
    );
    for (const sidebar of sidebars) {
      if (
        sidebar.dataset.open !== 'true' ||
        (sidebar.dataset.isFloating !== 'true' &&
          sidebar.dataset.layoutTransition !== 'overlay') ||
        win.getComputedStyle(sidebar).visibility === 'hidden'
      ) {
        continue;
      }
      const rect = sidebar.getBoundingClientRect();
      if (
        rect.width <= 0 ||
        rect.height <= 0 ||
        rect.bottom <= top ||
        rect.top >= bottom ||
        rect.right <= left ||
        rect.left >= right
      ) {
        continue;
      }
      if (sidebar.dataset.handlePosition === 'right') {
        left = Math.max(left, rect.right);
      } else if (sidebar.dataset.handlePosition === 'left') {
        right = Math.min(right, rect.left);
      }
    }
    const middle = (top + bottom) / 2;
    for (const rect of gfx?.viewport.overlayRects ?? []) {
      if (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.right > left &&
        rect.left < right &&
        rect.top > middle &&
        rect.top < bottom
      ) {
        bottom = Math.max(top, rect.top - OVERLAY_GAP);
      }
    }

    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);
    Object.assign(dialog.style, {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    });
    dialog.style.setProperty(
      '--maximap-size',
      `${maximapSize(width, height)}px`
    );
  };
  const schedule = () => {
    if (frame !== null) return;
    frame = win.requestAnimationFrame(() => {
      frame = null;
      update();
    });
  };
  const observer = new ResizeObserver(schedule);
  for (const element of [viewport, host, ...sidebars]) {
    if (element) observer.observe(element);
  }
  const attributes = new MutationObserver(schedule);
  for (const element of sidebars) {
    attributes.observe(element, {
      attributes: true,
      attributeFilter: [
        'data-open',
        'data-is-floating',
        'data-layout-transition',
        'data-transition-state',
      ],
    });
    element.addEventListener('transitionend', schedule);
  }
  win.addEventListener('resize', schedule);
  win.visualViewport?.addEventListener('resize', schedule);
  win.visualViewport?.addEventListener('scroll', schedule);
  doc.addEventListener('scroll', schedule, true);
  update();

  return () => {
    observer.disconnect();
    attributes.disconnect();
    for (const element of sidebars) {
      element.removeEventListener('transitionend', schedule);
    }
    if (frame !== null) win.cancelAnimationFrame(frame);
    win.removeEventListener('resize', schedule);
    win.visualViewport?.removeEventListener('resize', schedule);
    win.visualViewport?.removeEventListener('scroll', schedule);
    doc.removeEventListener('scroll', schedule, true);
  };
}
